import { Head } from "@inertiajs/react";
import { useEffect, useState, type ReactNode } from "react";
import { salonpageR2ImageUrl, encodeSalonpagePathId } from "@/lib/_url";
import { SalonpageFooter } from "./_footer";

const STYLES_PER_PAGE = 24;

interface Salon {
  id: string;
  name: string;
  nameKana: string;
}

interface Staff {
  id: string;
  name: string;
  kana: string;
  role: string;
  history: string;
  reserveUrl: string;
}

interface StyleItem {
  id: string;
  name: string;
  staffName: string;
  imageUrl: string;
  styleUrl: string;
  position: number;
}

interface Meta {
  title: string;
  description: string;
}

interface Props {
  salon?: Salon;
  staff?: Staff | null;
  styles?: StyleItem[];
  meta?: Meta;
}

export default function SalonStyles(props: Props) {
  if (!props.salon) return <NotFound label="Salon not found." />;

  const salon = props.salon;
  const staff = props.staff ?? null;
  const styles = props.styles ?? [];
  const meta = props.meta ?? { title: staff ? `${staff.name}のヘアスタイル` : `${salon.name} ヘアスタイル`, description: `${salon.name}のヘアスタイル一覧です。` };
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(styles.length / STYLES_PER_PAGE));
  const pagedStyles = styles.slice((page - 1) * STYLES_PER_PAGE, page * STYLES_PER_PAGE);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <>
      <Head title={meta.title}>
        <meta name="description" content={meta.description} />
      </Head>

      <div className="min-h-screen bg-gray-100 text-zinc-950 antialiased">
        <main className="mx-auto min-h-screen max-w-[448px] rounded-b-[22px] bg-white shadow-[0_0_24px_rgba(15,23,42,0.06)]">
          <Header salon={salon} staff={staff} title={staff ? `${staff.name}のヘアスタイル` : "ヘアスタイル"} />

          <section className="p-4">
            <div className="mb-4 flex items-center justify-between gap-4">
              <p className="text-sm font-bold text-zinc-500">
                <span className="tabular-nums text-zinc-950">{styles.length}</span> 件
              </p>
              {totalPages > 1 && <p className="text-xs font-bold text-zinc-400">{page} / {totalPages}</p>}
            </div>

            {pagedStyles.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {pagedStyles.map((style) => (
                  <a key={style.id} href={styleDetailUrl(salon, style)} className="block">
                    <img src={salonpageR2ImageUrl(salon.id, style.id, "thumbnail")} alt={style.name} className="aspect-[3/4] w-full rounded-md bg-gray-100 object-cover" />
                    <h2 className="mt-2 line-clamp-2 text-sm font-bold leading-5">{style.name}</h2>
                    {style.staffName && <p className="mt-1 text-xs font-medium text-zinc-400">{style.staffName}</p>}
                  </a>
                ))}
              </div>
            ) : (
              <div className="grid min-h-48 place-items-center rounded-md border border-dashed border-gray-200 bg-gray-50 px-6 text-center">
                <p className="text-sm font-bold leading-6 text-zinc-400">
                  {staff ? `${staff.name}の担当スタイルはまだ掲載されていません。` : "ヘアスタイルはまだ掲載されていません。"}
                </p>
              </div>
            )}

            {totalPages > 1 && (
              <Pagination
                className="mt-6"
                page={page}
                totalPages={totalPages}
                onChange={setPage}
              />
            )}
          </section>

        </main>
        <SalonpageFooter salon={salon} current="styles" />
      </div>
    </>
  );
}

function Header({ salon, staff, title }: { salon: Salon; staff: Staff | null; title: string }) {
  return (
    <header className="sticky top-0 z-10 border-b border-gray-100 bg-white/95 px-4 py-3 backdrop-blur">
      <a href={staff ? `/staffs/${encodeSalonpagePathId(staff.id)}` : `/salons/${encodeSalonpagePathId(salon.id)}`} className="text-xs font-bold text-zinc-400">
        {staff ? "← スタッフへ戻る" : "← サロンへ戻る"}
      </a>
      <h1 className="mt-2 text-xl font-bold tracking-tight">{title}</h1>
      <p className="mt-1 text-sm text-zinc-500">{salon.name}</p>
      {staff?.role && <p className="mt-2 text-xs font-bold text-zinc-400">{staff.role}</p>}
    </header>
  );
}

function NotFound({ label }: { label: string }) {
  return (
    <main className="grid min-h-screen place-items-center bg-gray-100 px-6 text-zinc-900">
      <p className="text-sm text-zinc-400">{label}</p>
    </main>
  );
}

function styleDetailUrl(salon: Salon, style: StyleItem) {
  return `/salons/${encodeSalonpagePathId(salon.id)}/styles/${encodeSalonpagePathId(style.id)}`;
}

function Pagination({ className = "", page, totalPages, onChange }: { className?: string; page: number; totalPages: number; onChange: (page: number) => void }) {
  return (
    <nav className={`flex items-center justify-center gap-1 ${className}`}>
      <PageButton disabled={page <= 1} onClick={() => onChange(1)}>«</PageButton>
      <PageButton disabled={page <= 1} onClick={() => onChange(page - 1)}>‹</PageButton>
      {paginationRange(page, totalPages).map((n) =>
        n === "..." ? (
          <span key={n} className="px-2 text-sm text-zinc-400">…</span>
        ) : (
          <PageButton key={n} active={n === page} onClick={() => onChange(n as number)}>{n}</PageButton>
        )
      )}
      <PageButton disabled={page >= totalPages} onClick={() => onChange(page + 1)}>›</PageButton>
      <PageButton disabled={page >= totalPages} onClick={() => onChange(totalPages)}>»</PageButton>
    </nav>
  );
}

function PageButton({ children, active, disabled, onClick }: { children: ReactNode; active?: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md border px-2 text-sm font-bold transition ${
        active
          ? "border-zinc-900 bg-zinc-950 text-white"
          : disabled
            ? "cursor-not-allowed border-zinc-200 text-zinc-300"
            : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"
      }`}
    >
      {children}
    </button>
  );
}

function paginationRange(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "...")[] = [1];
  if (current > 3) pages.push("...");
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
  if (current < total - 2) pages.push("...");
  if (total > 1) pages.push(total);
  return pages;
}
